import { reachGoal } from 'utils/metrica';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { LinkPopover, Send as SendIcon } from '@vedmarket/ui-kit';
import {formatReferenceCopy} from 'utils/typography';
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { ContentPaste as ContentPasteIcon } from '@vedmarket/ui-kit';
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ExpandMore as ExpandMoreIcon } from '@vedmarket/ui-kit';
import Divider from "@mui/material/Divider";
import { TurnedIn as TurnedInIcon } from '@vedmarket/ui-kit';

import {
  setLockedByActiveApplication,
  updateOrderSummaryByIdMutation,
  useBaseCurrency,
  useGetOrderSummaryByIdQuery,
  useGetOrderSummaryInvoicesByIdQuery,
  useLazyGetFinalReportDataQuery,
  useOrder,
  useSummary,
  useSummaryCompleted,
  useSummaryInvoices,
  useUpdateOrderMutation
} from "../../../../../api/calc";
import {isDemoPage, useGetCalcIdFromRoute} from "../../../../../utils/hooks";
import {useAuthContext} from "../../../../../context/AuthContext";
import { APPLICATION_STATUS, ROUTES } from '../../../../../config/constants'
import {
  applicationDetailNavigateOptions,
  applicationDetailPath,
} from '../../../../../utils/applicationRoutes'
import {isCalculationViewModeError, numberWithSpaces} from "../../../../../utils/helpers";
import {getPluralForm} from "../../../../../utils/validation";

import Notification, {NOTIFICATION_TYPE} from "../../../../common/notification";
import ModalBig from "../../../../common/modalBig";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store";
import { setAccessDenied, useUser, useUserPermission } from "../../../../../api/auth";
import {
  useCreateApplicationMutation,
  useListApplicationsQuery,
} from "../../../../../api/applications";

import { useApplicationsList } from '../../../../../api/applications';

import COLORS from "../../../../../styles/colors.scss";
import ModalSettings from "./modal_settings";
import './summary.scss';
import '../../../../common/modalAction/modalSmall.scss';
import { TableView as TableViewIcon } from '@vedmarket/ui-kit';
import { PictureAsPdf as PictureAsPdfIcon } from '@vedmarket/ui-kit';

const REGISTRATION_URL = 'https://my.vedmarket.ru/register'

export const enum DISTRIBUTION_TYPE {
  VOLUME = 'volume',
  PRICE = 'price',
}

export const enum ORDERS_TYPE {
  WITH_VAT = 'with_vat',
  WITHOUT_VAT = 'without_vat',
}

type FieldOption = {
  id: number;
  name: string;
  code: string;
  description: string;
};

const Summary = () => {

  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [ openModalSettings, setOpenModalSettings ] = React.useState(false);
  const [ createApplicationConfirmOpen, setCreateApplicationConfirmOpen ] = React.useState(false);
  const [ createApplication, { isLoading: isCreatingApplication } ] = useCreateApplicationMutation();
  const [ applicationNotice, setApplicationNotice ] = React.useState<CalculatorApplicationNotice<NOTIFICATION_TYPE> | null>(null);

  const orderId = useGetCalcIdFromRoute()

  const { user: authUser } = useAuthContext()
  const sessionUser = useUser()
  const isRegistered = Boolean(authUser?.id || sessionUser?.id)
  const canCreateSupplyApplication = useUserPermission('calc.create_supply_application')
  const canAccessSupplyApplicationsSection = useUserPermission('menu.applications')

  const { refetch } = useGetOrderSummaryInvoicesByIdQuery(orderId)
  const { data: summary, refetch: summaryRefetch } = useGetOrderSummaryByIdQuery(orderId)

  const summaryInfo = useSummaryInvoices();

  const summaryCompleted = useSummaryCompleted()

  const summaryOrder = useSummary();

  const baseCurrency = useBaseCurrency();

  const [ distributionType, setDistributionType ] = useState<DISTRIBUTION_TYPE>(DISTRIBUTION_TYPE.VOLUME);

  const [ ordersType, setOrdersType ] = useState<ORDERS_TYPE>(ORDERS_TYPE.WITH_VAT);

  const [ isExportingPdf, setIsExportingPdf ] = useState(false);
  const [ pdfError, setPdfError ] = useState(false);

  const [ getFinalReportData ] = useLazyGetFinalReportDataQuery();

  const [ updateOrderSummary ] = updateOrderSummaryByIdMutation()

  const order = useOrder()

  const [ updateOrder, { isLoading: isUpdatingOrder } ] = useUpdateOrderMutation()

  const printTitle = `${t('calc__text__summary__finalCalculationOfCargoDelivery')} №${order?.id}`;

  const canShowApplicationAction = isRegistered && Boolean(order?.id);

  const {
    isLoading: isApplicationsForCalculationLoading,
  } = useListApplicationsQuery(
    { calculation_id: order?.id, limit: 20 },
    { skip: !canShowApplicationAction || !canAccessSupplyApplicationsSection },
  );

  const applicationsForCalculation = useApplicationsList({
    params: { calculation_id: order?.id, limit: 20 },
  });

  const applicationActionResolved =
    !canShowApplicationAction || !isApplicationsForCalculationLoading;

  const activeApplicationForCalculation = applicationsForCalculation?.results?.find(
    (item) => item.status !== APPLICATION_STATUS.CLOSED,
  );

  const isApplicationBusy = isCreatingApplication || isUpdatingOrder;

  const isFinishedOrder =
    order?.status === 'done' || order?.status === 'completed';

  useEffect(() => {
    refetch();
  }, [ summaryCompleted ])

  useEffect(() => {
    summaryRefetch();
  }, [ ])

  if (!summaryOrder){
    return null;
  }

  const handleClick = async () => {
    const lockedByApplication = Boolean(order?.locked_by_active_application)
    if (!isFinishedOrder && !lockedByApplication) {
      const result = await updateOrder({
        status: 'done',
        id: orderId,
      })

      if (isCalculationViewModeError(result)) {
        return;
      }
    }

    setOpenModalSettings(true);

  }

  const handleOpenApplication = () => {
    if (!activeApplicationForCalculation) return;
    navigate(
      applicationDetailPath({ publicId: activeApplicationForCalculation.public_id }),
      applicationDetailNavigateOptions(false),
    );
  };

  const handleCreateApplicationClick = () => {
    if (!canCreateSupplyApplication || !order?.id || activeApplicationForCalculation) return;
    reachGoal({
      goal: 'razmestit_zayavku',
      params: {
        distribution_type: distributionType,
        orders_type: ordersType,
      },
    });
    setCreateApplicationConfirmOpen(true);
  };

  const handleConfirmCreateApplication = async () => {
    if (!order?.id) return;
    setApplicationNotice(null);
    dispatch(setAccessDenied(false));
    try {
      if (!isFinishedOrder) {
        const statusResult = await updateOrder({
          status: 'done',
          id: orderId,
        });
        if (isCalculationViewModeError(statusResult) || (statusResult && 'error' in statusResult)) {
          setApplicationNotice({ type: NOTIFICATION_TYPE.ERROR, text: t('applications__text__createError') });
          return;
        }
      }
      const created = await createApplication({ calculation_id: order.id }).unwrap();
      dispatch(setLockedByActiveApplication());
      setCreateApplicationConfirmOpen(false);
      setApplicationNotice({ type: NOTIFICATION_TYPE.SUCCESS, text: t('applications__text__createdSuccess') });
      navigate(
        applicationDetailPath({ publicId: created.public_id }),
        applicationDetailNavigateOptions(false),
      );
    } catch {
      dispatch(setAccessDenied(false));
      setApplicationNotice({ type: NOTIFICATION_TYPE.ERROR, text: t('applications__text__createError') });
    }
  };

  const handleChangeDistributionType = async (type: DISTRIBUTION_TYPE) => {
    const result = await updateOrderSummary({
      distribution_by_weight: type === DISTRIBUTION_TYPE.VOLUME,
      orderId: orderId
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    await refetch();
    await summaryRefetch();
    setDistributionType(type);
  }

  const handleChangeOrdersType = (type: ORDERS_TYPE) => {
    setOrdersType(type);
  }

  const handleDownloadPdf = async () => {
    if (isExportingPdf) {
      return;
    }

    setIsExportingPdf(true);
    setPdfError(false);

    try {
      const data = await getFinalReportData(orderId).unwrap();
      const { downloadSummaryPdf } = await import('./components/print_table/download-pdf');

      await downloadSummaryPdf({ data, title: printTitle });
    } catch {
      setPdfError(true);
    } finally {
      setIsExportingPdf(false);
    }
  }

  const PRODUCTS_DECLENSIONS: PluralForms = [ t('declension__text__itemOne'), t('declension__text__itemSeveral'), t('declension__text__itemMany') ];

  const totalProducts = summaryInfo?.reduce((sum, invoice) => sum + (invoice.products?.length || 0), 0);

  const totalInvoiceValue = summary?.summary?.total_amount_exchange;

  const RUB_DECLENSIONS: PluralForms = [ t('declension__text__rubOne'), t('declension__text__rubSeveral'), t('declension__text__rubMany') ];

  const getTotalInvoiceValueRub = () => {
    if (!totalInvoiceValue?.RUB) {
      return 0;
    }

    return ordersType === ORDERS_TYPE.WITH_VAT ? totalInvoiceValue['RUB'].value : totalInvoiceValue['RUB'].net_value;
  }

  return <>
    <div className='summary-calculator'>
      <Typography variant='h5' sx={{
        display: 'flex', alignItems: 'center', fontWeight: '700', padding: '30px 24px 4px'
      }}>
        <ContentPasteIcon
          style={{
            color: COLORS.GREEN,
            marginRight: '16px',
            width: 32,
            height: 33,
          }}/> {t('calc__text__summary__finalCalculationOfCargoDelivery')} №{order.id}
      </Typography>
      <Divider sx={{ marginTop: '20px' }}/>
      <div className='summary-calculator__notificationContainer'>
        <Notification
          type={NOTIFICATION_TYPE.WARNING}
          title={t('common__message__warning')}
          text={t('calc__text__summary__warningText')}
          sx={{marginTop: 3}}
          noCloseButton
        />
      </div>
      <div className='summary-calculator__sorting'>
        <FormControl sx={{width: '332px', boxSizing: 'border-box'}}>
          <InputLabel id='distribution-group-label'>{t('calc__text__summary__distribution')}</InputLabel>
          <Select
            labelId='distribution-group-label'
            id='distribution-group-label'
            // @ts-ignore
            value={distributionType}
            label={t('calc__text__summary__distribution')}
            // @ts-ignore
            onChange={({target: {value: type}}) => handleChangeDistributionType(type)}
          >
            <MenuItem key={DISTRIBUTION_TYPE.PRICE} value={DISTRIBUTION_TYPE.PRICE}>
              {t('calc__text__summary__distributionByPrice')}
            </MenuItem>
            <MenuItem key={DISTRIBUTION_TYPE.VOLUME} value={DISTRIBUTION_TYPE.VOLUME}>
              {t('calc__text__summary__distributionByVolume')}
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{width: '332px', boxSizing: 'border-box'}}>
          <InputLabel id='distribution-group-label'>{t('calc__text__summary__orders')}</InputLabel>
          <Select
            labelId='orders-group-label'
            id='orders-group-label'
            // @ts-ignore
            value={ordersType}
            label={t('calc__text__summary__distribution')}
            // @ts-ignore
            onChange={({target: {value: type}}) => handleChangeOrdersType(type)}
          >
            <MenuItem key={ORDERS_TYPE.WITH_VAT} value={ORDERS_TYPE.WITH_VAT}>
              {t('calc__text__summary__ordersWithVAT')}
            </MenuItem>
            <MenuItem key={ORDERS_TYPE.WITHOUT_VAT} value={ORDERS_TYPE.WITHOUT_VAT}>
              {t('calc__text__summary__ordersWithoutVAT')}
            </MenuItem>
          </Select>
        </FormControl>
      </div>
      {summaryInfo?.map((invoice, index) => (
        <div key={index} className='summary-calculator__info'>
          <Accordion sx={{
            marginBottom: 0,
            boxShadow: 'none',
            '&.Mui-expanded:last-of-type': {
              marginBottom: 0
            }
          }} defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              sx={{
                padding: '20px 24px 22px',
                '> div': {margin: '6px 0 !important'},
                '&.Mui-expanded': {
                  borderBottom: 'none'
                },
                '.Mui-expanded.MuiAccordionSummary-content': {
                  margin: 0,
                },
                '.MuiAccordionSummary-content': {
                  margin: 0,
                },
                alignItems: 'start'
              }}
            >
              <div className='summary-calculator__title'>
                <div className='summary-calculator__title-left'>
                  <TurnedInIcon style={{color: COLORS.GREEN, marginRight: '16px', marginTop: '8px' }}/>
                  <div>
                    <Typography variant='h6'>
                      {t('common__text__invoice')} №{invoice.invoice_num}
                    </Typography>
                    <Typography variant='caption' sx={{ lineHeight: 1.3, display: 'block' }}>
                      {`${invoice.products.length} ${getPluralForm(invoice.products.length, PRODUCTS_DECLENSIONS)}`}
                    </Typography>
                  </div>
                </div>
                <div className='summary-calculator__title-right'>
                  <Typography variant='h6' sx={{ marginTop: '2px' }}>
                    {numberWithSpaces(ordersType === ORDERS_TYPE.WITH_VAT ? invoice?.total_amount : invoice.net_total_amount)} {baseCurrency}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      textTransform: 'lowercase',
                      lineHeight: 1.3
                    }}>
                    {ordersType === ORDERS_TYPE.WITH_VAT ? t('calc__text__summary__ordersWithVAT') : t('calc__text__summary__ordersWithoutVAT')}
                  </Typography>
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails
              sx={{borderTop: `1px solid ${COLORS.SLIGHTLY_GRAY}`,
                marginLeft: 3,
                marginRight: 3,
                padding: 0,
              }}>
              <table className='summary-calculator__table'>
                <thead>
                  <tr>
                    <th>
                      <Typography variant='body2'>
                      №
                      </Typography>
                    </th>
                    <th>
                      <Typography variant='body2'>
                        {t('calc__text__packaging__nameItem')}
                      </Typography>
                    </th>
                    <th>
                      <Typography variant='body2'>
                        {t('calc__text__summary__count')}
                      </Typography>
                    </th>
                    <th>
                      <Typography variant='body2'>
                        {t('common__text__price')}, {invoice.currency}
                      </Typography>
                    </th>
                    <th>
                      <Typography variant='body2'>
                        {t('common__text__all')}, {invoice.currency}
                      </Typography>
                    </th>
                    {ordersType === ORDERS_TYPE.WITH_VAT && (
                      <th>
                        <Typography variant='body2'>
                          {t('common__text__vat')}, %
                        </Typography>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {invoice.products.map((product, index) => (
                    <tr key={index}>
                      <td>
                        <Typography variant='body2' sx={{color: COLORS.BLACK_DARK}}>
                          {index + 1}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant='body2' sx={{color: COLORS.BLACK_DARK}}>
                          {product.name}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant='body2' sx={{color: COLORS.BLACK_DARK}}>
                          {numberWithSpaces(product.pieces_total)}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant='body2' sx={{color: COLORS.BLACK_DARK}}>
                          {numberWithSpaces(ordersType === ORDERS_TYPE.WITH_VAT ? product.price_unit : product.net_price_unit)}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant='body2' sx={{color: COLORS.BLACK_DARK}}>
                          {numberWithSpaces(ordersType === ORDERS_TYPE.WITH_VAT ? product.total_amount : product.net_total_amount)}
                        </Typography>
                      </td>
                      {ordersType === ORDERS_TYPE.WITH_VAT && (
                        <td>
                          <Typography variant='body2' sx={{color: COLORS.BLACK_DARK}}>
                            {product.vat_rate}
                          </Typography>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </AccordionDetails>
          </Accordion>
        </div>
      ))}
      <div className='summary-calculator__buttons'>
        <div className='summary-calculator__title summary-calculator__title_final'>
          <div>
            <Typography variant='h6'>
              {t('common__text__inTotalCaps')}
            </Typography>
            {totalProducts && <Typography variant='caption' sx={{ lineHeight: 1.4, display: 'block', color: COLORS.LIGHT_TEXT_DISABLED }}>
              {`${totalProducts} ${getPluralForm(totalProducts, PRODUCTS_DECLENSIONS)}`}
            </Typography>}
          </div>
          {totalInvoiceValue && (
            <div>
              <Typography variant='h6' sx={{textAlign: 'right'}}>
                {numberWithSpaces(ordersType === ORDERS_TYPE.WITH_VAT ? totalInvoiceValue[baseCurrency].value : totalInvoiceValue[baseCurrency].net_value)} {baseCurrency}
              </Typography>
              <Typography variant='caption' sx={{lineHeight: 1.4, display: 'block', color: COLORS.LIGHT_TEXT_DISABLED}}>
                ≈ {numberWithSpaces(getTotalInvoiceValueRub())} {getPluralForm(getTotalInvoiceValueRub(), RUB_DECLENSIONS)},&nbsp;
                {ordersType === ORDERS_TYPE.WITH_VAT ? t('calc__text__summary__ordersWithVAT').toLowerCase() : t('calc__text__summary__ordersWithoutVAT').toLowerCase()}
              </Typography>
            </div>
          )}
        </div>
        {pdfError && (
          <Notification
            type={NOTIFICATION_TYPE.ERROR}
            noCloseButton
            text={<Typography variant='body1'>{t('common__text__error')}</Typography>}
          />
        )}
        <div className='summary-calculator__buttons-list'>
          <Button
            startIcon={<TableViewIcon style={{ fill: COLORS.BLUE, width: '24px', height: '24px' }} />}
            variant='outlined'
            onClick={handleClick}
            sx={{ width: '198px', letterSpacing: '0.46px' }}
          >
            {t('common__text__downloadExcel')}
          </Button>
          <Button
            startIcon={<PictureAsPdfIcon style={{ fill: COLORS.BLUE, width: '24px', height: '24px' }} />}
            variant='outlined'
            onClick={handleDownloadPdf}
            disabled={isExportingPdf}
            sx={{ width: '198px', letterSpacing: '0.46px' }}
          >
            {t('common__text__downloadPDF')}
          </Button>
        </div>
        <Button
          sx={{ maxHeight: '42px', marginTop: 1, minHeight: '42px' }}
          color='secondary'
          startIcon={
            applicationActionResolved
              ? <SendIcon />
              : <CircularProgress size={18} color='inherit' />
          }
          variant='contained'
          fullWidth
          disabled={
            !canShowApplicationAction
            || !applicationActionResolved
            || (
              !activeApplicationForCalculation
              && (isApplicationBusy || !canCreateSupplyApplication)
            )
          }
          onClick={
            activeApplicationForCalculation
              ? handleOpenApplication
              : handleCreateApplicationClick
          }
        >
          {applicationActionResolved && (
            activeApplicationForCalculation
              ? t('calc__text__summary__viewApplication')
              : t('calc__text__summary__createARequestForCargoDelivery')
          )}
        </Button>
        {!activeApplicationForCalculation && <div className='summary-calculator__application-help'>
          <LinkPopover
            linkText={t('calc__help__supplyApplicationLink')}
            text={<div className='summary-calculator__application-help-content'>
              <Typography variant='inherit'>
                {formatReferenceCopy(t('calc__help__supplyApplicationIntro'))}
              </Typography>
              <Typography variant='inherit'>
                {formatReferenceCopy(t('calc__help__supplyApplicationSubmit'))}
              </Typography>
            </div>}
          />
        </div>}
      </div>
      {isDemoPage() && (
        <Typography
          variant='body2'
          sx={{
            color: COLORS.LIGHT_TEXT_SECONDARY,
            display: 'block',
            margin: '0 24px 24px',
            textAlign: 'center',
          }}
        >
          {t('calc__text__demoWelcomeEditableStart')}{' '}
          <span className='summary-calculator__demo-editable-field'>
            {t('calc__text__demoWelcomeEditableProductQuantity')}
          </span>{' '}
          {t('calc__text__demoWelcomeEditableMiddle')}{' '}
          <span className='summary-calculator__demo-editable-field'>
            {t('calc__text__demoWelcomeEditablePrice')}
          </span>{' '}
          {t('calc__text__demoWelcomeEditableEnd')}{' '}
          <a
            className='summary-calculator__demo-editable-link'
            data-demo-allowed='registration'
            href={isRegistered ? `/${ROUTES.CABINET}/${ROUTES.CALCULATIONS}` : REGISTRATION_URL}
            rel='noreferrer'
            target='_blank'
          >
            {t(isRegistered ? 'calc__text__demoEditableFieldsCabinet' : 'calc__text__demoEditableFieldsRegister')}
          </a>
          {t('calc__text__demoEditableFieldsSuffix')}
        </Typography>
      )}
    </div>
    {applicationNotice && (
      <Notification
        type={applicationNotice.type}
        text={applicationNotice.text}
      />
    )}
    {openModalSettings && <ModalSettings isOpen={openModalSettings} onClose={() => setOpenModalSettings(false)}/>}
    <ModalBig
      isOpen={createApplicationConfirmOpen}
      onClose={() => setCreateApplicationConfirmOpen(false)}
      title={t('calc__text__summary__createApplicationConfirmTitle')}
      width={736}
      noPadding
      buttons={(
        <div className='modal-common__actions'>
          <Button
            variant='outlined'
            sx={{ minWidth: '238px', marginRight: 3 }}
            disabled={isApplicationBusy}
            onClick={() => setCreateApplicationConfirmOpen(false)}
          >
            {t('calc__text__summary__createApplicationConfirmCancel')}
          </Button>
          <Button
            variant='contained'
            fullWidth
            disabled={isApplicationBusy}
            onClick={handleConfirmCreateApplication}
          >
            {t('calc__text__summary__createApplicationConfirmAction')}
          </Button>
        </div>
      )}
    >
      <div className='summary-calculator__application-confirm-modal'>
        <Typography variant='body1' sx={{ mb: 2, display: 'block', width: '100%', maxWidth: 'none' }}>
          {t('calc__text__summary__createApplicationConfirmBody1')}
        </Typography>
        <Typography variant='body1' sx={{ mb: 2, display: 'block', width: '100%', maxWidth: 'none' }}>
          {t('calc__text__summary__createApplicationConfirmBody2')}
        </Typography>
        <Typography variant='body1' sx={{ display: 'block', width: '100%', maxWidth: 'none' }}>
          {t('calc__text__summary__createApplicationConfirmBody3')}
        </Typography>
      </div>
    </ModalBig>
  </>
}

export default Summary
